import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import api from 'api';
import { FEATURE } from 'constant/features';
import { SERVICE_PROVIDER_ROLES } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import type { ChatMessage, ChatReference, ChatResponse, ReplySuggestionResponse } from 'types';
import { hasFeature } from 'utils/features';
import { getErrorMessage, getErrorStatus } from 'utils/v2/errorHelper';

const POLL_INTERVAL_MS = 5000;
const SYSTEM_CHAT_CREATED = 'chat created!';

const isChatCreatedMessage = (message: ChatMessage) =>
    message.user_id === null && message.text?.trim().toLowerCase() === SYSTEM_CHAT_CREATED;

const mergeUniqueMessages = (current: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] => {
    if (!incoming.length) {
        return current;
    }

    const existingIds = new Set(current.map((message) => message.id));
    const uniqueIncoming = incoming.filter((message) => {
        if (existingIds.has(message.id)) {
            return false;
        }

        existingIds.add(message.id);
        return true;
    });

    if (!uniqueIncoming.length) {
        return current;
    }

    return [...current, ...uniqueIncoming];
};

const useClientViewChat = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const {
        id: userId,
        role,
        enabled_features: enabledFeatures,
    } = useSelector((state: RootState) => state.user.entity);

    const [isLoading, setIsLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [isSuggestingReply, setIsSuggestingReply] = useState(false);
    const [canRetryReplySuggestion, setCanRetryReplySuggestion] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [chatGroupId, setChatGroupId] = useState('');
    const [users, setUsers] = useState<string[]>([]);
    const [userIds, setUserIds] = useState<number[]>([]);
    const [hasAccess, setHasAccess] = useState(false);
    const [canAccessBackoffice, setCanAccessBackoffice] = useState(true);
    const replySuggestionRequestIdRef = useRef(0);
    const replySuggestionInFlightRef = useRef(false);

    const resetChat = useCallback(() => {
        replySuggestionRequestIdRef.current += 1;
        replySuggestionInFlightRef.current = false;
        setMessages([]);
        setChatGroupId('');
        setUsers([]);
        setUserIds([]);
        setHasAccess(false);
        setCanAccessBackoffice(true);
        setIsSuggestingReply(false);
        setCanRetryReplySuggestion(false);
    }, []);

    const getApplicationChat = useCallback(
        async (applicationId: number, isBackOffice: boolean) => {
            resetChat();
            setIsLoading(true);

            try {
                const response = await api.chatNew.get_application_chat_messages(
                    applicationId.toString(),
                    isBackOffice
                );
                const data = response.data as ChatResponse;
                const newChatGroupId = data.chat_group_id?.toString() ?? '';

                setChatGroupId(newChatGroupId);
                setMessages(data.messages ?? []);
                setUsers(data.users ?? []);
                setUserIds(data.user_ids ?? []);
                setCanAccessBackoffice(Boolean(data.can_access_backoffice));

                if (newChatGroupId) {
                    try {
                        const accessRes = await api.chatNew.has_access(
                            parseInt(newChatGroupId, 10)
                        );
                        setHasAccess(Boolean(accessRes.data?.has_access));
                    } catch {
                        setHasAccess(false);
                    }
                }
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToLoadChat')),
                        type: 'error',
                    })
                );
            } finally {
                setIsLoading(false);
            }
        },
        [dispatch, resetChat, translate]
    );

    useEffect(() => {
        if (!chatGroupId || !userId) {
            return;
        }

        let lastPolledAt = new Date().toISOString();
        let requestInFlight = false;
        const groupId = parseInt(chatGroupId, 10);

        const interval = setInterval(async () => {
            if (requestInFlight || Number.isNaN(groupId)) {
                return;
            }

            requestInFlight = true;
            const pollStartedAt = new Date().toISOString();

            try {
                const res = await api.chatNew.poll_messages(groupId, lastPolledAt);
                const newMessages = (res.data ?? []) as ChatMessage[];

                if (newMessages.length) {
                    setMessages((prev) => mergeUniqueMessages(prev, newMessages));
                }

                lastPolledAt = pollStartedAt;
            } catch (error) {
                console.error('Polling error:', error);
            } finally {
                requestInFlight = false;
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [chatGroupId, userId]);

    const handleSendMessage = useCallback(
        async (text: string, references?: ChatReference[]) => {
            const trimmed = text.trim();
            const groupId = parseInt(chatGroupId, 10);

            if (!trimmed || !chatGroupId || Number.isNaN(groupId)) {
                return;
            }

            const tempId = -Date.now();
            const refsToSend = references?.length ? references : undefined;
            const optimisticMessage: ChatMessage = {
                id: tempId,
                chat_group_id: groupId,
                text: trimmed,
                user: null,
                user_id: userId,
                created_at: new Date().toISOString(),
                read: true,
                references: refsToSend,
            };

            setMessages((prev) => [...prev, optimisticMessage]);
            setSendingMessage(true);

            try {
                const response = await api.chatNew.send_message(groupId, trimmed, refsToSend);
                const realId = response.data.message_id as number;

                setMessages((prev) => {
                    const realIdExists = prev.some((message) => message.id === realId);
                    if (realIdExists) {
                        return prev.filter((message) => message.id !== tempId);
                    }

                    return prev.map((message) =>
                        message.id === tempId ? { ...message, id: realId } : message
                    );
                });
            } catch (error) {
                setMessages((prev) => prev.filter((message) => message.id !== tempId));
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToSendMessage')),
                        type: 'error',
                    })
                );
            } finally {
                setSendingMessage(false);
            }
        },
        [chatGroupId, dispatch, translate, userId]
    );

    const handleSuggestReply = useCallback(
        async (
            candidateId: number,
            applicationId?: number
        ): Promise<ReplySuggestionResponse | null> => {
            if (
                !candidateId ||
                !SERVICE_PROVIDER_ROLES.includes(role) ||
                !hasFeature(enabledFeatures, FEATURE.REPLY_SUGGESTIONS) ||
                replySuggestionInFlightRef.current
            ) {
                return null;
            }

            const requestId = replySuggestionRequestIdRef.current + 1;
            replySuggestionRequestIdRef.current = requestId;
            replySuggestionInFlightRef.current = true;
            setIsSuggestingReply(true);
            setCanRetryReplySuggestion(false);

            try {
                const payload = applicationId ? { application_id: applicationId } : {};
                const response = await api.chatNew.get_reply_suggestion(candidateId, payload);

                if (replySuggestionRequestIdRef.current !== requestId) {
                    return null;
                }

                return response.data;
            } catch (error) {
                if (replySuggestionRequestIdRef.current !== requestId) {
                    return null;
                }

                const isRetryable = getErrorStatus(error) === 502;
                setCanRetryReplySuggestion(isRetryable);
                dispatch(
                    setAlert({
                        message: isRetryable
                            ? translate('couldNotDraftReplyRetry')
                            : getErrorMessage(error, translate('failedToDraftReply')),
                        type: 'error',
                    })
                );
                return null;
            } finally {
                if (replySuggestionRequestIdRef.current === requestId) {
                    replySuggestionInFlightRef.current = false;
                    setIsSuggestingReply(false);
                }
            }
        },
        [dispatch, enabledFeatures, role, translate]
    );

    const handleDeleteMessage = useCallback(
        async (messageId: number) => {
            try {
                await api.chatNew.update_message(messageId, translate('deleted'));
                setMessages((prev) =>
                    prev.map((message) =>
                        message.id === messageId
                            ? { ...message, text: translate('deleted') }
                            : message
                    )
                );
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToDeleteMessage')),
                        type: 'error',
                    })
                );
                throw error;
            }
        },
        [dispatch, translate]
    );

    const visibleMessages = useMemo(() => {
        const seen = new Set<number>();
        const unique = messages.filter((message) => {
            if (isChatCreatedMessage(message) || seen.has(message.id)) {
                return false;
            }

            seen.add(message.id);
            return true;
        });

        return unique.sort(
            (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
        );
    }, [messages]);

    return {
        isLoading,
        messages: visibleMessages,
        chatGroupId,
        users,
        userIds,
        hasAccess,
        canAccessBackoffice,
        sendingMessage,
        isSuggestingReply,
        canRetryReplySuggestion,
        getApplicationChat,
        handleSendMessage,
        handleSuggestReply,
        handleDeleteMessage,
        resetChat,
    };
};

export default useClientViewChat;
